import { useState } from "react";
import { api } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { PageHeader, Card, Badge, Select, Spinner, EmptyState } from "../../components/ui";
import { date } from "../../lib/format";

type Status = "PENDING" | "ACCEPTED" | "REVOKED" | "EXPIRED";
interface Invitation { id: string; email: string; role: string; clientName: string | null; status: Status; expiresAt: number; createdAt: number }

const TONE: Record<Status, "ok" | "warn" | "danger" | "neutral"> = { PENDING: "warn", ACCEPTED: "ok", REVOKED: "danger", EXPIRED: "neutral" };
const LABEL: Record<Status, string> = { PENDING: "Pendiente", ACCEPTED: "Aceptada", REVOKED: "Revocada", EXPIRED: "Caducada" };

export function CrmInvitations() {
  const { data, loading, reload } = useApi<{ data: Invitation[] }>("/api/invitations");
  const [filter, setFilter] = useState("");
  const rows = (data?.data ?? []).filter((i) => !filter || i.status === filter);

  async function resend(id: string) { await api.post(`/api/invitations/${id}/resend`); await reload(); }
  async function revoke(id: string) { await api.post(`/api/invitations/${id}/revoke`); await reload(); }

  return (
    <>
      <PageHeader title="Invitaciones" subtitle="Accesos al portal enviados a clientes y personal" />
      <div className="mb-4 max-w-xs">
        <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">Todos los estados</option>
          {(Object.keys(LABEL) as Status[]).map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
        </Select>
      </div>

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState>No hay invitaciones.</EmptyState>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full whitespace-nowrap text-sm">
            <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-5 py-3 font-medium">Email</th>
                {/* Secondary columns are hidden on phones so resend/revoke stay on screen. */}
                <th className="hidden px-5 py-3 font-medium sm:table-cell">Destino</th>
                <th className="px-5 py-3 font-medium">Estado</th>
                <th className="hidden px-5 py-3 font-medium sm:table-cell">Creada</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((i) => (
                <tr key={i.id} className="hover:bg-canvas">
                  <td className="max-w-40 truncate px-5 py-3 font-medium sm:max-w-none">{i.email}</td>
                  <td className="hidden px-5 py-3 text-muted sm:table-cell">{i.clientName ?? (i.role === "CUSTOMER" ? "—" : "Personal · " + i.role)}</td>
                  <td className="px-5 py-3"><Badge tone={TONE[i.status]}>{LABEL[i.status]}</Badge></td>
                  <td className="hidden px-5 py-3 text-muted sm:table-cell">{date(i.createdAt)}</td>
                  <td className="px-5 py-3 text-right">
                    {(i.status === "PENDING" || i.status === "EXPIRED") && (
                      <span className="flex justify-end gap-3">
                        <button onClick={() => void resend(i.id)} className="text-xs font-medium text-accent-600 hover:underline">Reenviar</button>
                        <button onClick={() => void revoke(i.id)} className="text-xs font-medium text-muted hover:text-danger hover:underline">Revocar</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
